/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils'
import {
  $createParagraphNode,
  $insertNodes,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  LexicalCommand,
  LexicalEditor,
} from 'lexical';
import { forwardRef, useEffect, useRef } from 'react';
import { $createInlineImageNode, InlineImageNode, InlineImagePayload, Position } from './ccf-inline-image-node';
import { CcfImageIcon } from '@nice-ccf/ui-controls';
// import { CcfAssignmentAction, getIsImageNotSupported, getNonIncomingActiveContactInSelectedInteraction, updateInlineImageToBeUploaded } from '../../ccf-assignment-panel/ccf-assignment-panel.slice';
import { CcfLogger } from '@nice-ccf/agent-sdk';


// Type for payload when inserting inline image
export type InsertInlineImagePayload = Readonly<InlineImagePayload>;

// Command for inserting inline image
export const INSERT_INLINE_IMAGE_COMMAND: LexicalCommand<InsertInlineImagePayload> =
  createCommand('INSERT_INLINE_IMAGE_COMMAND');

// Props for CcfAttachmentButton component
export interface CcfInlineImageButtonProps {
  activeEditor: LexicalEditor;
}

/**
 * Forwarding ref for CcfInsertInlineImageButton component.
 * @param ref - Reference to the component.
 * @example - CcfInsertInlineImageButton
 */
export const CcfInsertInlineImageButton = forwardRef<HTMLLabelElement, CcfInlineImageButtonProps>(
  ({ activeEditor }, ref) => {
    const ccfLogger: CcfLogger = new CcfLogger('App.react-ui-component', 'ccf-inline-image-plugin');
    const hasModifier = useRef(false);

    /**
     * Function to load and insert image into the editor.
     * @param files - Image files to be loaded.
     * @example - loadInlineImage
     */
    const loadInlineImage = (files: any) => {
      try {
        if (files !== null) {
          for (const file of Array.from(files)) {
            const reader = new FileReader();
            reader.onload = function (event) {
              const src = event?.target?.result as string; // This is the data URL of the image
              const altText = files.name; // You can set the alt text to the file name or customize it
              const showCaption = true; // Set this based on your requirements
              const position = 'left' as Position; // Set this based on your requirements
              const payload = { altText, src, showCaption, position };
              // Dispatch the command to insert the inline image
              activeEditor.dispatchCommand(
                INSERT_INLINE_IMAGE_COMMAND,
                payload
              );
              const fileInput = document.getElementById('ccf-inline-images');
              // Reset the files property
              if (fileInput instanceof HTMLInputElement) {
                fileInput.value = '';
                fileInput.files = null;
              }
              return '';
            };
            reader.readAsDataURL(file as Blob);
          }
        }
      } catch (error) {
        // Handle any errors that might occur during file reading
        ccfLogger.error(
          'loadInlineImage',
          `error while reading file - ${JSON.stringify(error)}`
        );
      }
    };

    useEffect(() => {
      hasModifier.current = false;

      /**
       * Effect to handle the alt key modifier.
       * @param e - Keyboard event.
       * @example - handler
       */
      const handler = (e: KeyboardEvent) => {
        hasModifier.current = e.altKey;
      };
      document.addEventListener('keydown', handler);
      return () => {
        document.removeEventListener('keydown', handler);
      };
    }, [activeEditor]);

    return (
        <><input
        hidden
        id="ccf-inline-images"
        multiple
        type="file"
        accept="image/*"
        data-testid='inline-files'
        onChange={(event) => loadInlineImage(event?.target?.files)} /><section>
          <label htmlFor="ccf-inline-images" style={{ width: 'max-content', display: 'flex' }} ref={ref}>
            <CcfImageIcon style={{ ...{ color: '#424242', cursor: 'hand' } }} />
          </label>
        </section></>
    );
  }
);

/**
 * Main CcfInlineImagePlugin component.
 * @param captionsEnabled - Flag to determine if captions are enabled.
 * @example - CcfInlineImagePlugin
 */
export default function CcfInlineImagePlugin({
  captionsEnabled,
}: {
  captionsEnabled?: boolean;
}): JSX.Element | null {
  // Using LexicalComposerContext hook to get the editor instance
  const [editor] = useLexicalComposerContext();
  const ccfLogger: CcfLogger = new CcfLogger('App.react-ui-component', 'ccf-inline-image-plugin');

  useEffect(() => {
    // Checking if InlineImageNode is registered on the editor
    if (!editor.hasNodes([InlineImageNode])) {
      ccfLogger.error('CcfInlineImagePlugin', 'ImageNode not registered on editor');
    }

    // Registering the command for inserting inline image
    return mergeRegister(
      editor.registerCommand<InsertInlineImagePayload>(
        INSERT_INLINE_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createInlineImageNode(payload);
          $insertNodes([imageNode]);

          // Wrapping the image node in a paragraph if it's at the root or shadow root
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      )
    );
  }, [captionsEnabled, editor]);

  return null;
}
